import type { FC, ReactElement } from 'react';

import type { IHelpChapter } from '../../../../api/fetchers/help/fetcher';
import type { ChipValueType } from '../../../../store/slices/chips/types';
import { useIsLargerThanWide, useIsLargerThanExtraExtraWide } from '../../../../styles/hooks/media';
import { IconButton, Icons } from '../../../controls/iconButton/IconButton';
import { Currency } from '../../../currency/Currency';
import { BarChipStack } from '../../../game/chipStack/barChipStack';
import { RevolverChipStack } from '../../../game/chipStack/revolverChipStack';
import { GameButton } from '../../../game/gameButton/gameButton';
import { TotalBetWing } from '../../../game/totalBetWing/TotalBetWing';
import { TrafficLight } from '../../../game/trafficLight/TrafficLight';
import { baseTrafficLightConfig } from '../../../game/trafficLight/types';
import { HelpComponentWrapper } from '../../utils/HelpComponentWrapper';
import { HelpText } from '../../utils/HelpText';

interface HelpChapterPlacingBetsBetOnAllProps {
  betOnAllButton?: ReactElement;
  betOnAllContent?: IHelpChapter;
}

interface HelpChapterPlacingBetsChipStackProps {
  chips: ChipValueType[];
  selectedChip: ChipValueType;
  previousChip?: ChipValueType | null;
  nextChip: ChipValueType | null;
}

interface HelpChapterPlacingBetsProps {
  content: IHelpChapter | null;
  tableName: ReactElement;
  tableBetLimits: { min: number; max: number };
  locale: string;
  currency: string;
  betOnAllProps?: HelpChapterPlacingBetsBetOnAllProps;
  isStorybook?: boolean;
  totalBetHidden?: boolean;
  chipStackProps?: HelpChapterPlacingBetsChipStackProps;
}

export const HelpChapterPlacingBets: FC<HelpChapterPlacingBetsProps> = ({
  content,
  tableName,
  tableBetLimits,
  locale,
  currency,
  betOnAllProps,
  isStorybook,
  totalBetHidden = false,
  chipStackProps,
}) => {
  const isWide = useIsLargerThanWide();
  const minWidthForWide = isWide ? '450px' : '100%';
  const isExtraExtraWide = useIsLargerThanExtraExtraWide();
  const minWidthForExtraWide = isExtraExtraWide ? '550px' : minWidthForWide;

  if (!content) return null;

  return (
    <div>
      <p>
        <HelpText>{String(content.tableInfo)}</HelpText>
      </p>
      <HelpComponentWrapper
        style={{
          backgroundColor: isStorybook ? '#464646' : 'unset',
          paddingBottom: isStorybook ? '1em' : '0',
        }}
      >
        <div
          style={{
            display: 'flex',
            margin: '0',
            marginTop: 'var(--core-spacing-regular)',
            padding: '0',
            font: 'inherit',
            fontSize: 'var(--core-font-size-large)',
            color: 'var(--core-color-text)',
            border: 'none',
            borderRadius: '0',
            background: 'none',
            alignItems: 'center',
            appearance: 'none',
            cursor: 'pointer',
            textShadow: 'var(--core-text-shadow-header)',
          }}
        >
          <span>{tableName}</span>
          <span>&nbsp;&nbsp;</span>
          <span>
            <Currency value={tableBetLimits.min} currency={currency} locale={locale} avoidTrailingZero />
            {' - '}
            <Currency value={tableBetLimits.max} currency={currency} locale={locale} avoidTrailingZero />
          </span>
        </div>
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.gameInformation)}</HelpText>
      </p>

      <p>
        <HelpText>{String(content.gameIndicator)}</HelpText>
      </p>
      <HelpComponentWrapper style={{ marginTop: '1rem' }} isOriginalHeight={false} minWidth={minWidthForExtraWide}>
        <TrafficLight
          status={{ ...baseTrafficLightConfig.PLACEYOURBETS, render: undefined }}
          timerStartTime={Date.now()}
          isPaused={true}
          progressOffset={1 - 10 / 15}
          animationDuration={10_000}
          onScheduledStatusChange={(): void => {}}
        />
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.placeBets)}</HelpText>
      </p>
      <p>
        <HelpText>{String(content.lastBets)}</HelpText>
      </p>
      <HelpComponentWrapper style={{ marginTop: '1rem' }} isOriginalHeight={false} minWidth={minWidthForExtraWide}>
        <TrafficLight
          status={{ ...baseTrafficLightConfig.LASTBETS, render: undefined }}
          timerStartTime={Date.now()}
          isPaused={true}
          progressOffset={1 - 5 / 15}
          animationDuration={5_000}
          onScheduledStatusChange={(): void => {}}
        />
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.bettingTime)}</HelpText>
      </p>
      <HelpComponentWrapper style={{ marginTop: '1rem' }} isOriginalHeight={false} minWidth={minWidthForExtraWide}>
        <TrafficLight
          status={{ ...baseTrafficLightConfig.BETSACCEPTED, render: undefined }}
          animationDuration={10_000}
          timerStartTime={Date.now()}
          onScheduledStatusChange={(): void => {}}
        />
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.chipDisplay)}</HelpText>
      </p>
      <HelpComponentWrapper>
        {isWide ? (
          <BarChipStack
            chips={
              chipStackProps?.chips ?? [
                { value: 0.2, weight: 1 },
                { value: 1, weight: 2 },
                { value: 5, weight: 3 },
                { value: 10, weight: 4 },
                { value: 50, weight: 5 },
                { value: 100, weight: 6 },
                { value: 250, weight: 7 },
                { value: 500, weight: 8 },
              ]
            }
            areBetsOpen={true}
            selectedChip={chipStackProps?.selectedChip ?? { value: 1, weight: 2 }}
            previousChip={chipStackProps?.previousChip ?? null}
            nextChip={chipStackProps?.nextChip ?? null}
            onChipSelect={(): null => null}
          />
        ) : (
          <RevolverChipStack
            chips={chipStackProps?.chips ?? []}
            areBetsOpen={true}
            selectedChip={chipStackProps?.selectedChip ?? { value: 1, weight: 2 }}
            previousChip={chipStackProps?.previousChip ?? { value: 0.2, weight: 1 }}
            nextChip={chipStackProps?.nextChip ?? { value: 5, weight: 3 }}
            onChipSelect={(): null => null}
            disabled={false}
          />
        )}
      </HelpComponentWrapper>

      {/*Sweet Bonanza specific button*/}
      {betOnAllProps?.betOnAllButton ? (
        <>
          <p>
            <HelpText>{String(betOnAllProps?.betOnAllContent) ?? String(content.betOnAll)}</HelpText>
          </p>
          <HelpComponentWrapper>{betOnAllProps?.betOnAllButton}</HelpComponentWrapper>
        </>
      ) : null}

      <p>
        <HelpText>{String(content.rebetButton)}</HelpText>
      </p>
      <HelpComponentWrapper>
        <GameButton icon={Icons.Update} disabled={false} border pulse/>
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.doubleButton)}</HelpText>
      </p>
      <HelpComponentWrapper>
        <GameButton icon={Icons.DoubleBet} disabled={false} border pulse/>
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.undoButton)}</HelpText>
      </p>
      <HelpComponentWrapper>
        <GameButton icon={Icons.Undo} disabled={false} border />
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.balanceIndicator)}</HelpText>
      </p>
      <p>
        <HelpText>{String(content.keyF5)}</HelpText>
      </p>
      <p>
        <HelpText>{String(content.cashierButton)}</HelpText>
      </p>
      <HelpComponentWrapper>
        <IconButton icon={Icons.Cashier} />
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.betIndicator)}</HelpText>
      </p>
      <HelpComponentWrapper
        style={{
          backgroundColor: isStorybook ? '#464646' : 'unset',
          padding: isStorybook ? '0.5em' : '0',
        }}
      >
        {!totalBetHidden ? (
          <>
            <TotalBetWing totalBet={<Currency value={10} currency={currency} locale={locale} />} />
          </>
        ) : null}
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.gameID)}</HelpText>
      </p>
      <HelpComponentWrapper>
        <div
          style={{
            width: '155px',
            height: '38px',
            background: 'rgba(0, 2, 10, 0.3)',
            display: 'flex',
            color: '#ededed',
            fontSize: 'var(--core-font-size-small)',
            textShadow: '1px 1px 3px #000000',
          }}
        >
          <span style={{ margin: 'auto' }}>12:36 ID: 1234567890</span>
        </div>
      </HelpComponentWrapper>
      <p>
        <HelpText>{String(content.gameIDReference)}</HelpText>
      </p>
    </div>
  );
};
